import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import AbcNotation from '@/components/AbcNotation'
import PlayerControls from '@/components/PlayerControls'
import TagChip from '@/components/TagChip'
import VoiceBadge from '@/components/VoiceBadge'
import { useWarmupPlayback, PlaybackSpec } from '@/hooks/useWarmupPlayback'
import { rangeLabel } from '@/lib/pitch'
import { cn } from '@/lib/utils'
import type { Warmup } from '@/lib/types'

export default function WarmupDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [warmup, setWarmup] = useState<Warmup | null>(null)
  const [error, setError] = useState('')

  // Range fields are deprecated but still returned; fall back to sane defaults for playback.
  const spec: PlaybackSpec | null = useMemo(
    () =>
      warmup
        ? {
            abcSource: warmup.abcSource,
            referenceMidi: warmup.referenceMidi ?? 60,
            rangeLowMidi: warmup.rangeLowMidi ?? 55,
            rangeHighMidi: warmup.rangeHighMidi ?? 67,
            stepSemitones: warmup.stepSemitones,
            direction: warmup.direction,
            tempoQpm: warmup.tempoQpm,
          }
        : null,
    [warmup]
  )
  const playback = useWarmupPlayback(spec)

  useEffect(() => {
    apiFetch<Warmup>(`/warmups/${slug}`)
      .then(setWarmup)
      .catch((e) => setError(e.message))
  }, [slug])

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <p className="text-destructive">{error}</p>
        <Link to="/warmups" className="mt-4 inline-block underline">Back to the library</Link>
      </div>
    )
  }

  if (!warmup) {
    return <div className="mx-auto max-w-xl px-4 py-24 text-center spec-label">Loading…</div>
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <Link to="/warmups" className="spec-label hover:text-foreground">← Library</Link>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <h1 className="font-display text-3xl sm:text-4xl font-bold">{warmup.name}</h1>
        <VoiceBadge voice={warmup.voice} />
      </div>
      {warmup.description && <p className="mt-2 text-muted-foreground">{warmup.description}</p>}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {warmup.tags.map((t) => (
          <TagChip key={t.slug} tag={t} size="sm" />
        ))}
      </div>

      <div className="mt-6 rounded-lg border bg-card p-4">
        <div className={cn('coil-bar', playback.isPlaying && 'is-hot')} />
        <div className="py-3">
          <AbcNotation abc={warmup.abcSource} transpose={playback.displayOffset} />
        </div>
        <div className={cn('coil-bar', playback.isPlaying && 'is-hot')} />
        <p className="mt-3 spec-label text-center">
          Range {rangeLabel((spec!.rangeLowMidi) + playback.nudge, (spec!.rangeHighMidi) + playback.nudge)}
          {' · '}step {warmup.stepSemitones > 0 ? '+' : ''}{warmup.stepSemitones} st · {warmup.direction.replace('_', ' + ').toLowerCase()}
        </p>
      </div>

      <div className="mt-6">
        <PlayerControls spec={spec!} playback={playback} />
      </div>

      {warmup.purpose && (
        <div className="mt-6 rounded-lg border bg-card p-4">
          <p className="spec-label mb-1">Why this works</p>
          <p className="text-sm text-muted-foreground">{warmup.purpose}</p>
        </div>
      )}
    </div>
  )
}
